import { api } from '../lib/api-client';
import { readApiResult, type DataResult } from '../lib/data-result';
import { escapeHtml } from '../lib/html';
import { submissionStatusChip } from '../lib/status-ui';
import { renderProductObjectMeta } from '../../../src/lib/ui/product-object';
import type { SubmissionCard } from '../types/api';

type SubmissionSort = 'newest' | 'oldest' | 'title';
type SubmissionStats = {
  total: number;
  pending: number;
  approved: number;
  published: number;
};

async function loadSubmissions(): Promise<DataResult<SubmissionCard[]>> {
  return readApiResult(
    () => api.submissions.list({}),
    'Failed to load submissions',
    { map: (data) => data.submissions || [] },
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr.includes('Z') ? dateStr : dateStr + 'Z');
    if (Number.isNaN(date.getTime())) return '-';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (error) {
    return '-';
  }
}

function filterSubmissions(input: {
  submissions: readonly SubmissionCard[];
  status: string;
  search: string;
  sort: string;
}): SubmissionCard[] {
  const search = input.search.trim().toLowerCase();
  const sort = input.sort as SubmissionSort;
  let filtered = [...input.submissions];

  if (input.status !== 'all') {
    filtered = filtered.filter((submission) => submission.status === input.status);
  }

  if (search) {
    filtered = filtered.filter((submission) => (submission.issue_title || '').toLowerCase().includes(search));
  }

  return filtered.sort((a, b) => {
    if (sort === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    if (sort === 'title') return (a.issue_title || '').localeCompare(b.issue_title || '');
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

function submissionStats(submissions: readonly SubmissionCard[]): SubmissionStats {
  return {
    total: submissions.length,
    pending: submissions.filter((submission) => submission.status === 'submitted').length,
    approved: submissions.filter((submission) => submission.status === 'approved').length,
    published: submissions.filter((submission) => submission.status === 'published').length,
  };
}

function renderEmpty(): string {
  return `
    <div class="text-center py-16">
      <i class="fas fa-inbox text-3xl text-zinc-600 mb-3"></i>
      <p class="text-sm text-zinc-400 font-medium mb-1">No submissions found</p>
      <p class="text-xs text-zinc-500">Try adjusting your filters or search</p>
    </div>`;
}

function renderSubmissionItem(submission: SubmissionCard): string {
  const issueTitle = escapeHtml(submission.issue_title || '');
  const title = issueTitle || `Submission #${submission.id}`;
  const shortHash = submission.head_commit_sha ? submission.head_commit_sha.slice(0, 7) : `#${submission.id}`;
  const submissionHref = '/submissions/' + submission.id;
  const issueMeta = renderProductObjectMeta({
    type: 'issue',
    id: submission.issue_id || null,
    label: submission.issue_id ? null : 'Unlinked issue',
  });
  const commitMeta = renderProductObjectMeta({
    type: 'commit',
    id: shortHash,
    className: 'font-mono',
  });
  const skillMeta = submission.skill_name || submission.skill_id
    ? renderProductObjectMeta({
        type: 'skill',
        id: submission.skill_id || null,
        name: submission.skill_name || null,
      })
    : '';

  return `
    <a href="${submissionHref}" class="submission-row animate-in" aria-label="View submission ${submission.id}">
      <div class="submission-row-main">
        <div class="submission-row-top">
          <span class="submission-id">#${submission.id}</span>
          <h3 class="submission-title">${title}</h3>
        </div>
        <div class="submission-meta">
          ${issueMeta}
          ${commitMeta}
          <span><i class="fas fa-clock"></i>${formatDate(submission.created_at)}</span>
          ${skillMeta}
        </div>
      </div>
      <div class="submission-row-right">
        ${submissionStatusChip(submission.status)}
        <span class="submission-row-action">View submission <i class="fas fa-arrow-right"></i></span>
      </div>
    </a>`;
}

function renderList(submissions: readonly SubmissionCard[]): string {
  if (!submissions.length) return renderEmpty();
  return submissions.map((submission) => renderSubmissionItem(submission)).join('');
}

function renderError(message: string): string {
  return `
    <div class="text-center py-16">
      <i class="fas fa-exclamation-circle text-3xl text-red-400 mb-3"></i>
      <p class="text-sm text-red-400 font-medium mb-1">Error loading submissions</p>
      <p class="text-xs text-zinc-500 mb-4">${escapeHtml(message)}</p>
      <button onclick="location.reload()" class="btn-ghost text-sm">
        <i class="fas fa-refresh mr-2"></i>Retry
      </button>
    </div>`;
}

window.SubmissionsPageData = {
  loadSubmissions,
};

window.SubmissionsPageRenderers = {
  filterSubmissions,
  submissionStats,
  renderList,
  renderError,
};
